import React from 'react';
import styles from './ReportCard.module.css';
import { useCartStatus } from '@/hooks/useCartStatus';
import { useSnackbar } from '@/context/SnackbarContext';

interface Report {
  slug: string;
  title: string;
  primary_industry_name: string;
  price_amount: number;
  content_id: number;
  legacy_report_id?: number;
  published_year: number;
  published_at?: string;
  discounted_price?: number;
  discounted_id?: number | string;
  discounted_percent?: number;
  cover_image_url?: string;
  summary?: string;
  metadata_json?: {
    cover_image_url?: string;
    image_url?: string;
  };
}


interface ReportCardProps {
  report: Report;
  onAddToCart?: (contentId: number, price: number, imageUrl?: string) => void | Promise<void>;
  isCsrMode?: boolean;
  /** Fired for each action on the card — open the report, request a sample, enquire, add to cart (search click tracking). */
  onAction?: (action: 'open' | 'sample' | 'enquiry' | 'cart') => void;
}

const ReportCard: React.FC<ReportCardProps> = ({ report, onAddToCart, isCsrMode, onAction }) => {
  const { isInCart, isLoading, addToCart } = useCartStatus(report.content_id);
  const { showSnackbar } = useSnackbar();

  const hasDiscount = (report?.discounted_price ?? 0) > 0;

  const discountedPrice = hasDiscount ? report.discounted_price || 0 : 0;

  const formatWithMonthYear = (dateStr?: string, year?: number) => {
    if (dateStr) {
      try {
        const date = new Date(dateStr);
        // Check if date is valid
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('default', { month: 'long', year: 'numeric' });
        }
      } catch (e) {
        // ignore error
      }
    }
    return year;
  };

  const handleAddToCartClick = async () => {
    onAction?.('cart');
    // Extract cover image from top level or metadata_json
    const coverImage = report.cover_image_url || report.metadata_json?.cover_image_url || report.metadata_json?.image_url;

    // If item is already in cart, navigate to order summary
    if (isInCart) {
      const params = new URLSearchParams();
      params.set('title', report.title);
      params.set('slug', report.slug);
      params.set('content_id', report.content_id.toString());
      if (coverImage) params.set('image', coverImage);

      window.location.href = `/order-summary?${params.toString()}`;
      return;
    }

    if (isLoading) return;

    try {
      // If parent provides callback, use it (for backward compatibility)
      // This prevents duplicate API calls
      if (onAddToCart) {
        const priceToUse = hasDiscount && discountedPrice > 0
          ? discountedPrice
          : report.price_amount;
        await onAddToCart(report.content_id, priceToUse, coverImage);
      } else {
        // Otherwise use the hook's addToCart with price validation
        const priceToUse = hasDiscount && discountedPrice > 0
          ? discountedPrice
          : report.price_amount;

        // Ensure price is valid before calling addToCart
        if (!priceToUse || priceToUse <= 0) {
          showSnackbar("Invalid price. Cannot add to cart.", 'error');
          return;
        }

        await addToCart(priceToUse, 'USD', 1, coverImage);
        showSnackbar("Report added to cart successfully", 'success');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      showSnackbar("Unable to add report to cart", 'error');
    }
  };

  return (
    <div className={styles.report_info}>
      <h4>
        <a href={isCsrMode ? `/report_csr/${report.slug}` : `/report/${report.slug}`} onClick={() => onAction?.('open')} className={styles.reportlink}>
          {report.title}
          {report.summary && ` - ${report.summary}`}
        </a>
      </h4>
      <h4 style={{ color: "#1B8E86" }}>{report.primary_industry_name}</h4>
      <h4>
        <span
          style={{
            color: "#15404F",
            fontWeight: "bold",
            textDecoration: hasDiscount ? "line-through" : "none",
            textDecorationColor: "#fc5a5a",
          }}
        >
          ${report.price_amount}
        </span>
        {hasDiscount && discountedPrice > 0 && (
          <span
            style={{
              color: "#fc5a5a",
              fontWeight: "bold",
              marginLeft: "10px",
            }}
          >
            ${discountedPrice}
          </span>
        )}
        <span style={{ color: "#000000" }}> | </span>
        <span style={{ color: "#15404F" }}>{formatWithMonthYear(report.published_at, report.published_year)}</span>
      </h4>

      <div className={styles.report_actions}>
        <a
          id="btnview"
          onClick={() => onAction?.('sample')}
          href={`/sample_request?id=${report.legacy_report_id || report.content_id}&type=smp&name=${encodeURIComponent(report.summary ? `${report.title} - ${report.summary}` : report.title)}`}
          target="_blank"
          className="btn btnsuc small"
        >
          Request a Sample
        </a>

        <a
          href={isCsrMode ? `/report_csr/${report.slug}` : `/report/${report.slug}`} onClick={() => onAction?.('open')}
          className={`btn btnsuc small ${styles.viewreportbtn}`}
        >
          View Report
        </a>

        <a
          id="contactbtn"
          onClick={() => onAction?.('enquiry')}
          href={`/Enquiry?id=${report.legacy_report_id || report.content_id}&type=smp&name=${encodeURIComponent(report.title)}`}
          className="btn btnsuc small"
          target="_blank"
          rel="noopener noreferrer"
        >
          Enquire Before Buying
        </a>

        <span className={styles.addtocartbtn}>
          <button
            className={`btnsuc small ${isInCart ? styles.inCart : ''}`}
            onClick={handleAddToCartClick}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
              backgroundColor: isInCart ? '#1B8E86' : undefined,
              cursor: isInCart ? 'pointer' : undefined
            }}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : isInCart ? (
              <>
                ✓ View in Cart
              </>
            ) : (
              'Add to Cart'
            )}
          </button>
        </span>
      </div>
    </div>
  );
};

export default ReportCard;
